// One intent, live: what was paid and is coming, each step with its transaction, the time it
// took, and the outcome: received (against the quote) or refunded.

import { useEffect, useRef, useState } from "react";
import { CHAINS, chainByGumId, knownSrcToken, knownToken, type Chain, type Token } from "../chains";
import { fmtElapsed, prettyUnits, short } from "../format";
import { TYPICAL_CREDIT, assetIdOf } from "../jupnet-receive";
import { fmtMs, isGumDeposit, progress, type Step } from "../progress";
import { fmtBps, paidLine, receipt } from "../terms";
import { trackIntent, type TrackState } from "../track";
import { USD, isBasketRow } from "../basket";
import { base58, nativeTokenAddr } from "../wire";
import { ChainLogo, IconAlert, IconCheck, IconExternal, IconUndo, Spinner, TokenOnChain } from "./icons";
import { useNow } from "./hooks";
import { CopyButton } from "./Wallets";

export function Tracker({ api, hash, jupnetSrc, onNew }: { api: string; hash: string; jupnetSrc: Chain | null; onNew: () => void }) {
  const [st, setSt] = useState<TrackState | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => {
    stop.current?.();
    setSt(null);
    setErr(null);
    setNote(null);
    stop.current = trackIntent(api, hash, { onState: setSt, onNote: setNote, onError: setErr });
    return () => stop.current?.();
  }, [api, hash]);
  const now = useNow();

  return (
    <div className="tracker" aria-live="polite">
      {st ? (
        <TrackView st={st} now={now} jupnetSrc={jupnetSrc} />
      ) : err ? null : (
        <div className="tracker-loading">
          <Spinner size={28} />
          <p>{note ?? "Looking up your swap…"}</p>
        </div>
      )}
      {st && note && <p className="fine center">{note}</p>}
      {err && (
        <p className="alert error" role="alert">
          {err}
        </p>
      )}
      <div className="tracker-foot">
        <span className="mono muted" title={hash}>
          Intent {short(hash, 6)}
        </span>
        <CopyButton text={hash} />
        <CopyButton text={`${location.origin}${location.pathname}?intent=${hash}`} label="Copy link" />
      </div>
      <button className="btn primary block" onClick={onNew}>
        New swap
      </button>
    </div>
  );
}

const amountText = (v: bigint | string | null | undefined, tok: Token | undefined) => (v == null ? "—" : tok ? `${prettyUnits(v, tok.decimals)} ${tok.sym}` : `${v} (raw units)`);

export function TrackView({ st, now, jupnetSrc }: { st: TrackState; now: number; jupnetSrc: Chain | null }) {
  const row = st.row;
  const p = progress(row, st.events);
  const preset = chainByGumId(row.src_chain_id);
  const srcC = preset?.family === "jupnet" && jupnetSrc ? jupnetSrc : preset;
  const dstC = chainByGumId(row.dst_chain_id);
  const deposit = isGumDeposit(row);
  const dstPreset = dstC ? knownToken(dstC, nativeTokenAddr(row.dst_token, dstC.family)) : undefined;
  // received on Jupnet: named as Jupnet credits it (deposited USDC is unified USD)
  const dstTok = dstPreset && deposit ? { ...dstPreset, sym: assetIdOf(dstPreset) } : dstPreset;
  const r = receipt(row, st.quote);
  // a Jupnet USD basket: its stablecoins, each in its own units (the wire names their USD sum)
  const basket = isBasketRow(row.input_token);
  const components = (row.input_components ?? []).map((c) => ({ token: srcC ? knownSrcToken(srcC, c.token) : undefined, amount: c.amount }));
  // what was paid: the token started with (hosted swap, linked), else the escrowed stable
  const paid = (() => {
    const l = paidLine(row, st.quote);
    if (!srcC) return undefined;
    const stable = basket ? USD : srcC.family === "jupnet" ? knownSrcToken(srcC, nativeTokenAddr(row.input_token, "jupnet")) : srcC.stable;
    const t = l.token === "stable" ? stable : knownSrcToken(srcC, l.token) ?? knownToken(srcC, l.token);
    return { label: l.label, token: t, amount: l.amount };
  })();
  const refundTok = basket ? { ...USD, sym: "stablecoins" } : srcC?.family === "jupnet" ? knownSrcToken(srcC, nativeTokenAddr(row.input_token, "jupnet")) : srcC?.stable;
  const started = Date.parse(row.created_at);
  const status = p.userDone ? (p.lane === "refund" ? "refunded" : "done") : p.lane === "refund" ? "refunding" : "pending";
  const elapsed = p.totalMs ?? (p.userDone ? undefined : now - started);

  return (
    <>
      <div className={`outcome ${status}`}>
        <span className="outcome-icon" aria-hidden>
          {status === "done" ? <IconCheck size={26} /> : status === "refunded" ? <IconUndo size={24} /> : status === "refunding" ? <IconAlert size={24} /> : <Spinner size={26} />}
        </span>
        <h2>
          {status === "done"
            ? deposit
              ? "Credited on Jupnet"
              : "Swap complete"
            : status === "refunded"
              ? "Refunded"
              : status === "refunding"
                ? row.state === "expired"
                  ? "No solver filled in time"
                  : "Refund in progress"
                : p.steps.find((s) => s.key === "fulfilled")?.status === "done"
                  ? "Waiting for Jupnet to credit it"
                  : "Waiting for a solver"}
        </h2>
        <p className="muted">
          {status === "done"
            ? p.totalMs !== undefined
              ? `Took ${fmtMs(p.totalMs)}`
              : "Delivered"
            : status === "refunded"
              ? `Your ${refundTok?.sym ?? "funds"} came back to you.`
              : status === "refunding"
                ? "Your funds come back automatically. Nothing to do."
                : `${elapsed !== undefined ? fmtElapsed(elapsed) : ""} · fills usually land within seconds`}
        </p>
      </div>

      <div className="route-summary">
        <div className="route-side">
          {srcC && paid?.token ? <TokenOnChain sym={paid.token.sym} chain={srcC.key} size={36} /> : srcC && <ChainLogo chain={srcC.key} size={36} />}
          <span className="route-amt">{paid ? amountText(paid.amount, paid.token) : "—"}</span>
          <span className="muted">{srcC?.label ?? `chain ${row.src_chain_id}`}</span>
        </div>
        <span className="route-arrow" aria-hidden>
          →
        </span>
        <div className="route-side">
          {dstC && dstTok ? <TokenOnChain sym={dstTok.sym} chain={deposit ? "jupnet" : dstC.key} size={36} /> : dstC && <ChainLogo chain={dstC.key} size={36} />}
          <span className="route-amt">{r.received !== undefined ? amountText(r.received, dstTok) : `≈ ${amountText(r.quoted ?? r.floor, dstTok)}`}</span>
          <span className="muted">{deposit ? `Jupnet via ${dstC?.label ?? row.dst_chain_id}` : (dstC?.label ?? `chain ${row.dst_chain_id}`)}</span>
        </div>
      </div>

      {deposit && status !== "refunded" && status !== "refunding" && (
        <p className="jupnet-receive-head tracker-jupnet">
          <ChainLogo chain="jupnet" size={18} />
          <span>
            {status === "done" ? "You received " : "You'll receive "}
            <strong>{r.received !== undefined ? amountText(r.received, dstTok) : `≈ ${amountText(r.quoted ?? r.floor, dstTok)}`}</strong> on Jupnet
            {status === "done" ? "" : <span className="muted"> · deposited on {dstC?.label ?? "the route's chain"}, then credited on Jupnet, {TYPICAL_CREDIT}</span>}
          </span>
        </p>
      )}

      {row.last_error && !p.final && (
        <p className="alert warn" role="status">
          Retrying automatically: {row.last_error}
        </p>
      )}

      <ol className="steps">
        {p.steps.map((s) => (
          <StepRow key={s.key} s={deposit ? { ...s, label: depositStepLabel(s, srcC, dstC) } : s} chain={s.key === "credited" ? CHAINS.jupnet : s.side === "src" ? srcC : s.side === "dst" ? dstC : undefined} />
        ))}
      </ol>

      <dl className="receipt">
        {paid && (
          <div>
            <dt>{paid.label}</dt>
            <dd>{amountText(paid.amount, paid.token)}</dd>
          </div>
        )}
        {basket && components.length > 0 && (
          <div>
            <dt>Paid with</dt>
            <dd>{components.map((c) => amountText(c.amount, c.token && { ...c.token, sym: c.token.name ?? c.token.sym })).join(" + ")}</dd>
          </div>
        )}
        {r.quoted !== undefined && (
          <div>
            <dt>Quoted</dt>
            <dd>
              {amountText(r.quoted, dstTok)}
              {st.quote && <span className="muted"> · {st.quote.solver}</span>}
            </dd>
          </div>
        )}
        {r.expectedAtActual !== undefined && (
          <div>
            <dt title="The quoted rate on what the source swap actually delivered">Expected at your actual input</dt>
            <dd>{amountText(r.expectedAtActual, dstTok)}</dd>
          </div>
        )}
        <div>
          <dt>Guaranteed at least</dt>
          <dd>{amountText(r.floor, dstTok)}</dd>
        </div>
        {r.received !== undefined && (
          <div className="strong">
            <dt>{deposit ? "Deposited for you" : "Received"}</dt>
            <dd>
              {amountText(r.received, dstTok)}
              {r.vsQuoteBps !== undefined && <span className={`delta-pill ${r.vsQuoteBps < 0 ? "neg" : "pos"}`}>{fmtBps(r.vsQuoteBps)} vs quote</span>}
              {r.vsExpectedBps !== undefined && <span className="muted block-sm">{fmtBps(r.vsExpectedBps)} vs expected at your input</span>}
            </dd>
          </div>
        )}
        {deposit && (
          <div>
            <dt>Jupnet account</dt>
            <dd className="mono">{short(base58(row.dst_recipient), 6)}</dd>
          </div>
        )}
        <div>
          <dt>Status</dt>
          <dd>
            <code>{row.state}</code> <span className="muted">· {st.via === "stream" ? "live" : "polling"}</span>
          </dd>
        </div>
      </dl>
    </>
  );
}

/** A received-on-Jupnet step, named by its chain: locked on the source, deposited on the route,
 *  credited on Jupnet (with its usual time while it is pending). */
export function depositStepLabel(s: Step, srcC?: Chain, dstC?: Chain): string {
  if (s.key === "opened" && srcC) return `Locked on ${srcC.label}`;
  if (s.key === "fulfilled" && dstC) return `Deposited on ${dstC.label}`;
  if (s.key === "credited") return s.status === "done" ? "Credited on Jupnet" : `Credited on Jupnet (${TYPICAL_CREDIT})`;
  return s.label;
}

function StepRow({ s, chain }: { s: Step; chain?: Chain }) {
  const link = s.tx && chain?.explorerTx ? chain.explorerTx(s.tx) : null;
  return (
    <li className={`step ${s.status}`}>
      <span className="step-dot" aria-hidden>
        {s.status === "done" ? <IconCheck size={12} /> : s.status === "active" ? <span className="pulse" /> : null}
      </span>
      <span className="step-label">
        {s.label}
        <span className="sr-only"> — {s.status === "done" ? "done" : s.status === "active" ? "in progress" : "pending"}</span>
      </span>
      <span className="step-meta">
        {s.delta !== undefined && <span className="muted">+{fmtMs(s.delta)}</span>}
        {s.status === "active" && s.at === undefined && <span className="muted" aria-hidden>{"…"}</span>}
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" aria-label={`${s.label}: view transaction`}>
            <IconExternal size={14} />
          </a>
        ) : s.tx ? (
          // a transaction on a chain without an explorer link: the signature, to copy
          <CopyButton text={s.tx} label={short(s.tx, 4)} />
        ) : null}
      </span>
    </li>
  );
}
